export async function getFileFromWebServer<R = string>(
	file: string,
	transformation?: (content: string) => R
): Promise<R> {
	const response = await fetch(file, {
		headers: {
			"Content-Type": "application/json",
		},
	});
	if (transformation === JSON.parse) {
		if (shouldProxyJSON(file)) {
			return createViewsProxy(response);
		} else {
			const json_value = await response.json();
			return json_value;
		}
	} else {
		return (await response.text()) as unknown as R;
	}
}

export async function getFilesFromWebserver<R = string>(
	files: string[],
	transformation?: (content: string) => R
): Promise<Record<string, R>> {
	const result = await Promise.all(
		files.map((file) => getFileFromWebServer(file, transformation).then<[string, R]>((content) => [file, content]))
	);
	return result.reduce((p, c) => ((p[c[0]] = c[1]), p), {} as Record<string, R>);
}

function shouldProxyJSON(file: string) {
	return file.endsWith("generated.alan.json");
}

async function createViewsProxy(response: Response): Promise<any> {
	const generated_view = {
		dependencies: {},
		views: {},
	};

	const reader = response.body!.pipeThrough(new TextDecoderStream());
	const text_reader = reader.getReader();
	const view_content = await parseViewContentLiterally(text_reader);
	const initializedViews = new Map<string, any>();

	for (const key of Object.keys(view_content)) {
		Object.defineProperty(generated_view.views, key, {
			get: () => {
				if (initializedViews.has(key)) {
					return initializedViews.get(key);
				} else {
					const parsed_view = JSON.parse(view_content[key]());
					initializedViews.set(key, parsed_view);
					return parsed_view;
				}
			},
			enumerable: true,
			configurable: true,
		});
	}
	return generated_view;
}

async function parseViewContentLiterally(stream: ReadableStreamDefaultReader<string>) {
	/* eslint-disable no-constant-condition */
	let current_value = "";
	let position = 0;
	const views: Record<string, () => string> = {};

	const values: string[] = [];
	while (true) {
		const new_value = await stream.read();
		if (new_value.done) {
			break;
		} else {
			values.push(new_value.value);
		}
	}

	let current_index = 0;
	let current_value_length = 0;
	const readValue = () => {
		position = 0;
		current_value = values[current_index++];
		current_value_length = current_value.length;
		return current_value;
	};

	const assertEqual = (expected: string, actual: string) => {
		if (expected === actual) return;
		throw new Error(`Expected value: ${expected} but got: ${actual}`);
	};

	const nextChar = (): string => {
		if (position === current_value_length) readValue();
		return current_value[position++];
	};

	const readStringLiteral = () => {
		// Assume that the starting quotes have been read.
		const value: string[] = [];
		while (true) {
			ch = nextChar();
			if (ch === "\\") {
				value.push(ch);
				ch = nextChar();
				value.push(ch);
			} else if (ch === '"') {
				return value.join("");
			} else {
				value.push(ch);
			}
		}
	};

	const readStringLiteralPositions = () => {
		// Assume that the starting quotes have been read.
		let ch = "";
		while (true) {
			ch = nextChar();
			if (ch === "\\") {
				ch = nextChar();
			} else if (ch === '"') {
				return;
			}
		}
	};

	const readObjectLiteral = () => {
		const start_chunk = current_index - 1; // because current_index is looking at the next value
		const start = position;
		let ch = nextChar();
		let level = 1;
		assertEqual(ch, "{");
		while (true) {
			ch = nextChar();
			if (ch === undefined) {
				readValue();
				ch = nextChar();
			}
			switch (ch) {
				case '"':
					readStringLiteralPositions();
					break;
				case "{":
					level += 1;
					break;
				case "}":
					level -= 1;
					if (level === 0) {
						return {
							start_chunk: start_chunk,
							start: start,
							end_chunk: current_index - 1,
							end: position,
						};
					}
					break;
			}
		}
	};

	readValue();
	const header = '{"dependencies":{},"views":{';
	position = header.length;

	const getContent = (positions: { start_chunk: number; start: number; end_chunk: number; end: number }) => {
		let content = "";
		let pos = positions.start;
		for (let chunk = positions.start_chunk; chunk <= positions.end_chunk; chunk += 1) {
			if (chunk === positions.end_chunk) {
				content += values[chunk].substring(pos, positions.end);
			} else {
				content += values[chunk].substring(pos);
			}
			pos = 0;
		}
		return content;
	};
	let ch = nextChar();
	while (true) {
		if (ch === '"') {
			const view_name = readStringLiteral();
			ch = nextChar();
			assertEqual(ch, ":");
			const positions = readObjectLiteral();
			views[view_name] = () => getContent(positions);
			ch = nextChar();
		} else if (ch === ",") {
			ch = nextChar();
		} else if (ch === undefined || ch === "}") {
			return views;
		} else {
			ch = nextChar();
		}
	}
	/* eslint-enable no-constant-condition */
}
